import { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style";

import {
  AppBar,
  Avatar,
  Badge,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  AddCircleOutlineOutlined as AddCircleOutlineOutlinedIcon,
  AssignmentOutlined as AssignmentIcon,
  AssignmentInd as AssignmentIndIcon,
  AssignmentIndOutlined as AssignmentIndOutlinedIcon,
  BarChartOutlined as BarChartIcon,
  CampaignOutlined as CampaignOutlinedIcon,
  Campaign as CampaignIcon,
  CategoryOutlined as CategoryOutlinedIcon,
  Event as EventIcon,
  DonutSmall as DonutSmallIcon,
  DonutSmallOutlined as DonutSmallOutlinedIcon,
  Domain as DomainIcon,
  DomainOutlined as DomainOutlinedIcon,
  GroupOutlined as GroupIcon,
  InsertEmoticon as InsertEmoticonIcon,
  LogoutOutlined as LogoutOutlinedIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  ToggleOffOutlined as ToggleOffOutlinedIcon,
  ToggleOnOutlined as ToggleOnOutlinedIcon,
  Segment as SegmentIcon,
  Feed as FeedIcon,
  AssignmentTurnedInOutlined as AssignmentTurnedInOutlinedIcon,
  ContactPageOutlined as ContactPageOutlinedIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
} from "@mui/icons-material";

import InboxIcon from "@mui/icons-material/Inbox";

import Logo from "../../assets/images/symbol.svg";

import { stringAvatar } from "../../utils/stringAvatar";

import { useAuth } from "../../contexts/AuthContext";
import { useModal } from "../../contexts/ModalContext";
import { useDialog } from "../../contexts/DialogContext";

import Snackbar from "../../components/Snackbar";
import Modal from "../../components/Modal";
import Dialog from "../../components/Dialog";

import AddPerson from "../../views/Admin/Dashboard/modals/addPerson";
import AddOrganization from "../../views/Admin/Dashboard/modals/addOrganization";
import AddUser from "../../views/Admin/Dashboard/modals/addUser";
import AddNps from "../../views/Admin/Dashboard/modals/addNps";
import AddMessage from "../../views/Admin/Dashboard/modals/addMessage";
import Search from "../../views/Admin/Dashboard/Search";
import Segment from "../../views/Admin/Segment";
import FopagDetailDrawer from "../../views/Admin/Fopag/drawer/detail";

import { useThemeMode } from "../../contexts/ThemeModeContext";

const drawerCollapsedWidth = 70;
const drawerExpandedWidth = 240;

const firstNav = [
  {
    title: "Dashboard",
    icon: <CategoryOutlinedIcon />,
    link: "/",
  },
  {
    title: "Pessoas",
    icon: <GroupIcon />,
    link: "persons",
  },
  {
    title: "Organizações",
    icon: <DomainOutlinedIcon />,
    link: "organizations",
  },
  {
    title: "Usuários",
    icon: <AssignmentIndOutlinedIcon />,
    link: "users",
  },
];

const secondNav = [
  {
    title: "Pesquisa CSAT",
    icon: <InsertEmoticonIcon />,
    link: "surveys/csat",
  },
  {
    title: "Pesquisas NPS",
    icon: <DonutSmallOutlinedIcon />,
    link: "surveys/nps",
  },
  {
    title: "CES Produtos",
    icon: <BarChartIcon />,
    link: "surveys/ces-products",
  },
  {
    title: "Segmentos",
    icon: <SegmentIcon />,
    link: "segments",
  },
  // {
  //   title: "Tarefas",
  //   icon: <AssignmentIcon />,
  //   path: "",
  // },
  {
    title: "Comunicados",
    icon: <CampaignOutlinedIcon />,
    link: "messaging",
  },
  {
    title: "FOPAG",
    icon: <ContactPageOutlinedIcon />,
    link: "fopag",
  },
  {
    title: "Reservar Sala de Reunião",
    icon: <EventIcon />,
    link: "rooms",
  },
  // {
  //   title: "Tipos de Tarefa",
  //   icon: <AssignmentTurnedInOutlinedIcon />,
  //   link: "task-types",
  // },
];

function Admin(props: any) {
  const { window } = props;
  const navigate = useNavigate();
  const theme = useThemeMode();
  const auth = useAuth();
  const modal = useModal();
  const dialog = useDialog();
  const location = useLocation();
  const [anchorElUser, setAnchorElUser] = useState<null>(null);
  const [anchorElOpts, setAnchorElOpts] = useState<null>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const exportMenuOpen = Boolean(exportMenuAnchor);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDrawerHovered, setIsDrawerHovered] = useState(false);
  const currentDrawerWidth = isDrawerHovered
    ? drawerExpandedWidth
    : drawerCollapsedWidth;

  useEffect(() => {
    auth?.tokenVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenUserMenu = (event: any) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenOptsMenu = (event: any) => {
    setAnchorElOpts(event.currentTarget);
  };

  const handleCloseOptsMenu = () => {
    setAnchorElOpts(null);
  };

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const handleAddItem = (schema: string) => {
    switch (schema) {
      case "organization":
        modal?.open("Criar Organização", <AddOrganization />);
        break;
      case "user":
        modal?.open("Criar Usuário", <AddUser />);
        break;
      case "nps":
        modal?.open("Criar Pesquisa NPS", <AddNps />);
        break;
      case "segment":
        dialog?.open("Criar Segmento", <Segment />);
        break;
      case "messaging":
        modal?.open("Criar Campanha", <AddMessage />);
        break;

      default:
        modal?.open("Criar Pessoa", <AddPerson />);
        break;
    }

    handleCloseOptsMenu();
  };

  const columnMapping = {
    status: "Ativo",
    name: "Nome",
    reference: "Codigo",
    document: "CNPJ",
    created_at: "Data de registro CRM",
    init_date: "Inicio do Contrato",
    profile: "Carteira",
    profileTier: "Perfil de Relacionamento",
    offboarding: "Data de Offboarding",
    critical: "Estado Critico",
    dp_info: "FOPAG",
    dp_adiantamento: "Tem ADTO",
    dp_sindPatronal: "Sindicato Patronal",
    dp_sindClasse: "Sindicato de Classe",
    dp_fopagDataPGTO: "Data pagto FOPAG",
    dp_fopagDataADTO: "Data pagto ADTO",
    dp_fopagDataTypePGTO: "Tipo de data FOPAG",
    dp_fopagDataTypeADTO: "Tipo de data ADTO",
    dp_funcCLT: "CLT",
    dp_observations: "DP Observações",
    fiscal_isncricao_estadual: "Inscricao Estadual",
    fiscal_isncricao_municipal: "Inscricao Municipal",
    fiscal_login: "Inscricao Login",
    fiscal_password: "Inscricao Senha",
    fiscal_regime: "Regime",
    fiscal_codigo_simples: "Codigo SIMPLES",
    fiscal_area: "Area",
    address_zipcode: "CEP",
    address_street: "Rua",
    address_number: "Numero",
    address_complement: "Complemento",
    address_district: "Municipio",
    address_city: "Cidade",
    address_state: "Estado",
    contabil_forma_envio: "Forma de envio",
    contabil_controle_financeiro: "Controle Financeiro",
    contabil_sistema: "Sistema Contabil",
    contabil_login: "Sistema Contabil Login",
    contabil_password: "Sistema Contabil Senha",
    contabil_observations: "Contábil Observações",
    fiscal_observations: "Fiscal Observações",
    products_saveRH: "Produto - SAVE RH",
    products_saveFinanc: "Produto - SAVE Financeiro",
    products_nfsEmit: "Produto - Emissão NFS",
    products_auditoria: "Produto - Auditoria",
    products_pjSimples: "Produto - PJ Simples",
    products_essencial: "Produto - Essencial",
    products_det: "Produto - DET Monitoramento",
  };

  const formatDataForExport = (data: any[]) => {
    return data.map((item) => {
      const formattedItem: any = {};
      Object.keys(item).forEach((key) => {
        const friendlyName =
          columnMapping[key as keyof typeof columnMapping] || key;
        formattedItem[friendlyName] = item[key];
      });
      return formattedItem;
    });
  };

  const exportFullBaseExcel = async () => {
    try {
      const token = localStorage.getItem(
        `${process.env.REACT_APP_KEYNAME}token`,
      );
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/organizations/export-full-base/json`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const jsonData = await response.json();
        const formattedData = formatDataForExport(jsonData);

        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const file = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
        });
        FileSaver.saveAs(file, "base-completa-clientes.xlsx");
      }
    } catch (error) {
      console.error("Erro ao exportar base completa:", error);
    }
  };

  // const toggleTheme = () => {
  // };

  const toolbar = (
    <>
      <IconButton
        aria-label="open drawer"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{ mr: 2, display: { sm: "none" } }}
      >
        <MenuIcon />
      </IconButton>
      <Box sx={{ flexGrow: 0 }}>
        <Search />
      </Box>
      <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}></Box>
      <Box
        sx={{
          flexGrow: 1,
          display: { xs: "none", md: "flex" },
          paddingLeft: "2em",
        }}
      >
        <Tooltip title="Caixa de entrada">
          <IconButton>
            {/* <Badge color="quaternary" badgeContent={1}> */}
            <Badge color="quaternary">
              <InboxIcon sx={{ color: "#CCC" }} />
            </Badge>
          </IconButton>
        </Tooltip>
        {auth?.user.permission_level === "manager" && (
          <>
            <Tooltip title="Adição rápida">
              <IconButton
                onClick={(e) => {
                  handleOpenOptsMenu(e);
                }}
                sx={{ marginLeft: ".2em" }}
              >
                <AddCircleOutlineOutlinedIcon sx={{ color: "text.primary" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Exportar dados">
              <IconButton
                onClick={handleExportClick}
                sx={{ marginLeft: ".2em" }}
                aria-controls={exportMenuOpen ? "export-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={exportMenuOpen ? "true" : undefined}
              >
                <DownloadOutlinedIcon sx={{ color: "text.primary" }} />
              </IconButton>
            </Tooltip>
            <Menu
              id="export-menu"
              anchorEl={exportMenuAnchor}
              open={exportMenuOpen}
              onClose={handleExportClose}
              MenuListProps={{
                "aria-labelledby": "export-button",
              }}
            >
              <MenuItem
                onClick={() => {
                  exportFullBaseExcel();
                  handleExportClose();
                }}
              >
                <StorageIcon
                  sx={{ color: "text.primary", marginRight: ".5em" }}
                />
                <Typography textAlign="center">Base completa</Typography>
              </MenuItem>
            </Menu>
          </>
        )}
        <Menu
          sx={{ mt: "45px" }}
          id="menu-appbar"
          anchorEl={anchorElOpts}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          open={Boolean(anchorElOpts)}
          onClose={handleCloseOptsMenu}
        >
          <MenuItem
            onClick={() => {
              handleAddItem("person");
            }}
          >
            <PersonIcon sx={{ color: "text.primary", marginRight: ".5em" }} />
            <Typography textAlign="center">Pessoa</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleAddItem("organization");
            }}
          >
            <DomainIcon sx={{ color: "text.primary", marginRight: ".5em" }} />
            <Typography textAlign="center">Organização</Typography>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              handleAddItem("user");
            }}
          >
            <AssignmentIndIcon
              sx={{ color: "text.primary", marginRight: ".5em" }}
            />
            <Typography textAlign="center">Usuário</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleAddItem("nps");
            }}
          >
            <DonutSmallIcon sx={{ marginRight: ".5em" }} />
            <Typography textAlign="center">Pesquisa NPS</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleAddItem("segment");
            }}
          >
            <SegmentIcon sx={{ color: "text.primary", marginRight: ".5em" }} />
            <Typography textAlign="center">Segmento</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleAddItem("messaging");
            }}
          >
            <CampaignIcon sx={{ color: "text.primary", marginRight: ".5em" }} />
            <Typography textAlign="center">Comunicado</Typography>
          </MenuItem>
        </Menu>
      </Box>
      <Box
        sx={{
          flexGrow: 0,
          paddingLeft: "1em",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            paddingLeft: "1em",
            display: { xs: "none", md: "flex" },
          }}
        >
          <Typography
            variant="body2"
            textAlign="center"
            sx={{ color: "text.primary" }}
          >
            Olá, {auth?.user.nickname}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 0, paddingLeft: "1em" }}>
          <Tooltip title={auth?.user.name}>
            <IconButton
              onClick={(e) => {
                handleOpenUserMenu(e);
              }}
              sx={{ p: 0 }}
            >
              {/* <Avatar alt="John Doe" src="/static/images/avatar/2.jpg" /> */}
              <Avatar {...stringAvatar(auth?.user.name)} />
            </IconButton>
          </Tooltip>
          <Menu
            sx={{ mt: "45px" }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            {/* <MenuItem onClick={handleCloseUserMenu}>
              <SettingsIcon
                sx={{ color: "text.primary", marginRight: ".5em" }}
              />
              <Typography textAlign="center">Preferências</Typography>
            </MenuItem>
            <Divider /> */}
            <MenuItem onClick={auth?.logout}>
              <LogoutOutlinedIcon
                sx={{ color: "text.primary", marginRight: ".5em" }}
              />
              <Typography textAlign="center">Sair</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </>
  );

  const drawer = (
    <Stack spacing={1} sx={{ px: 0, py: 1 }}>
      <Box
        sx={{
          py: 2,
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <img
          src={Logo}
          alt={process.env.REACT_APP_NAME}
          style={{
            marginTop: "5px",
            width: "30px",
            height: "30px",
            display: "block",
            objectFit: "contain",
            flexShrink: 0,
          }}
        />
      </Box>
      <Divider />
      {firstNav.map((item, index) => (
        <Box
          key={`fnav${index}`}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            borderRadius: 2,
            px: 0,
            py: 0.25,
            minHeight: 40,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
          onClick={() => {
            navigate(item.link);
          }}
        >
          <Tooltip title={!isDrawerHovered ? item.title : ""} placement="right">
            <Box
              sx={{
                width: 40,
                minWidth: 40,
                maxWidth: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "secondary.main",
                flexShrink: 0,
                ml: "15px",
                "& svg": {
                  fontSize: 24,
                },
              }}
            >
              {item.icon}
            </Box>
          </Tooltip>
          <Typography
            variant="body2"
            color="text.primary"
            noWrap
            sx={{
              maxWidth: isDrawerHovered ? 170 : 0,
              opacity: isDrawerHovered ? 1 : 0,
              overflow: "hidden",
              ml: isDrawerHovered ? 1 : 0,
              transition: (muiTheme) =>
                muiTheme.transitions.create(
                  ["max-width", "opacity", "margin"],
                  {
                    duration: muiTheme.transitions.duration.standard + 180,
                    easing: muiTheme.transitions.easing.easeInOut,
                  },
                ),
            }}
          >
            {item.title}
          </Typography>
        </Box>
      ))}
      <Divider />
      {secondNav.map((item, index) => (
        <Box
          key={`snav${index}`}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            borderRadius: 2,
            px: 0,
            py: 0.25,
            minHeight: 40,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
          onClick={() => {
            navigate(item.link);
          }}
        >
          <Tooltip title={!isDrawerHovered ? item.title : ""} placement="right">
            <Box
              sx={{
                width: 40,
                minWidth: 40,
                maxWidth: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "secondary.main",
                flexShrink: 0,
                ml: "15px",
                "& svg": {
                  fontSize: 24,
                },
              }}
            >
              {item.icon}
            </Box>
          </Tooltip>
          <Typography
            variant="body2"
            color="text.primary"
            noWrap
            sx={{
              maxWidth: isDrawerHovered ? 170 : 0,
              opacity: isDrawerHovered ? 1 : 0,
              overflow: "hidden",
              ml: isDrawerHovered ? 1 : 0,
              transition: (muiTheme) =>
                muiTheme.transitions.create(
                  ["max-width", "opacity", "margin"],
                  {
                    duration: muiTheme.transitions.duration.standard + 180,
                    easing: muiTheme.transitions.easing.easeInOut,
                  },
                ),
            }}
          >
            {item.title}
          </Typography>
        </Box>
      ))}
      <Divider />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          borderRadius: 2,
          px: 0,
          py: 0.25,
          minHeight: 40,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
        onClick={() => {
          theme?.change();
        }}
      >
        <Tooltip
          title={!isDrawerHovered ? "Mode escuro" : ""}
          placement="right"
        >
          <Box
            sx={{
              width: 40,
              minWidth: 40,
              maxWidth: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "secondary.main",
              flexShrink: 0,
              ml: "15px",
            }}
          >
            {theme?.mode === "light" && <ToggleOffOutlinedIcon />}
            {theme?.mode === "dark" && <ToggleOnOutlinedIcon />}
          </Box>
        </Tooltip>
        <Typography
          variant="body2"
          color="text.primary"
          noWrap
          sx={{
            maxWidth: isDrawerHovered ? 170 : 0,
            opacity: isDrawerHovered ? 1 : 0,
            overflow: "hidden",
            ml: isDrawerHovered ? 1 : 0,
            transition: (muiTheme) =>
              muiTheme.transitions.create(["max-width", "opacity", "margin"], {
                duration: muiTheme.transitions.duration.standard + 180,
                easing: muiTheme.transitions.easing.easeInOut,
              }),
          }}
        >
          Modo escuro
        </Typography>
      </Box>
    </Stack>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: "background.default",
        minHeight: "100vh",
      }}
    >
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerCollapsedWidth}px)` },
          ml: { sm: `${drawerCollapsedWidth}px` },
          backgroundColor: "background.default",
          backgroundImage: "none",
          boxShadow: "none",
          paddingTop: ".5em",
        }}
      >
        <Toolbar>{toolbar}</Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerCollapsedWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerCollapsedWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: currentDrawerWidth,
              transition: (muiTheme) =>
                muiTheme.transitions.create("width", {
                  duration: muiTheme.transitions.duration.standard + 180,
                  easing: muiTheme.transitions.easing.easeInOut,
                }),
              overflowX: "hidden",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(120, 120, 120, 0.35) transparent",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(120, 120, 120, 0.35)",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "rgba(120, 120, 120, 0.5)",
              },
            },
          }}
          open
        >
          <Box
            onMouseEnter={() => {
              setIsDrawerHovered(true);
            }}
            onMouseLeave={() => {
              setIsDrawerHovered(false);
            }}
          >
            {drawer}
          </Box>
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerCollapsedWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
        <Snackbar />
        <Modal />
        <Dialog />
        <FopagDetailDrawer />
      </Box>
    </Box>
  );
}

export default Admin;
